import { JSX, useMemo } from "react"
import { Link, useNavigate } from "react-router-dom"
import { isFunction } from "lodash"
import Tooltip from "components/material/tooltip"
import { useAccountAlias } from "resources/account"
import { type Resource } from "../types"

type OpenBasket = (() => void) | boolean | undefined

interface ReturnValue {
  action: (() => void) | null
  component: JSX.Element | null
  url: string | null
}

// Centralizes how a resource is linked: returns a ready-to-render component (Link or disabled span),
// a navigation action, and the URL. Handles “no models” state, basket vs normal resources, and
// optional openBasket so callers can use component, action, or url as needed.
export function useResourceComponentLink(resource: Resource, hasModels: boolean, openBasket?: OpenBasket): ReturnValue {
  const navigate = useNavigate()
  const account = useAccountAlias()

  return useMemo (() => {
    if (!resource) {
      return { component: null, action: null, url: null }
    }

    const { shortType, alias, name } = resource
    const label = name || alias

    if (!hasModels) {
      const noModelsComponent = (
        <Tooltip title="Access to this resource is disabled because you do not have access to any risk model">
          <span>{label}</span>
        </Tooltip>
      )
      return { component: noModelsComponent, action: null, url: null }
    }

    const url = shortType === "b" ? `/${account}/composite/basket/${alias}` : `/${account}/${shortType}/${alias}`

    let action = () => navigate(url)
    let component = <Link to={url}>{label}</Link>

    function handleOpenBasket() {
      if (!openBasket && shortType !== "b") {
        return null
      }
      if (isFunction(openBasket)) {
        return openBasket()
      }
      return navigate(url)
    }

    if (shortType === "b") {
      action = () => handleOpenBasket()
      component = <a
        href="#"
        onClick={event => {
          event.preventDefault()
          event.stopPropagation()
          action()
        }}
      >
        {label}
      </a>
    }

    return { action, component, url }
  }, [account, navigate, openBasket, hasModels, resource])
}
